import type { CatalogRole } from '@/api'
import { tabStorage } from '@/lib/teammateHandoff'

/**
 * The Add a teammate form, kept for this browser tab while the person is away on a page the form sends them to (#2128):
 * a tool's Connect and "Create a team first" are full pages, so the form is saved before it leaves and put back when
 * it opens again. Taken out when it is put back, and cleared once the teammate is added or the form is cancelled.
 * Nothing here is sent anywhere.
 */
const KEY = 'zh.teammate-add-draft'

/** The job as the form holds it: a Catalog role (the whole role, so it shows before the Catalog answers) or one described. */
export type DraftJob = { kind: 'catalog'; role: CatalogRole } | { kind: 'described' }

export type TeammateFormDraft = {
    job: DraftJob | null
    jobSearch: string
    jobTitle: string
    jobLine: string
    keptTaskIds: string[]
    swapChoices: Record<string, string>
    /** The connection picked per tool slot. A pick no longer offered when the review runs again is ignored there. */
    slotChoices: Record<string, string>
    name: string
    nameTyped: boolean
    persona: string
    personaTyped: boolean
    teamId: string
    aiProvider: 'shared' | 'own'
    firstTask: string
    emailTyped: string
    emailEdited: boolean
    avatar: string | null
    money: string
    moneyTyped: boolean
    slugTyped: string
    slugEdited: boolean
    extraTeamIds: string[]
    extraRoleIds: string[]
}

type DraftStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const text = (value: unknown): string => (typeof value === 'string' ? value : '')
const flag = (value: unknown): boolean => value === true
const ids = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string' && id !== '') : []

function choices(value: unknown): Record<string, string> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).filter(
            (entry): entry is [string, string] => typeof entry[1] === 'string',
        ),
    )
}

function draftJob(value: unknown): DraftJob | null {
    const { kind, role } = (value ?? {}) as Record<string, unknown>
    if (kind === 'described') return { kind }
    if (kind !== 'catalog' || !role || typeof role !== 'object') return null
    const { id, name } = role as Record<string, unknown>
    return typeof id === 'string' && id && typeof name === 'string' ? { kind, role: role as CatalogRole } : null
}

/** Keeps the form for this tab, replacing any earlier one. Leaving still works without storage; the form is lost. */
export function keepTeammateDraft(draft: TeammateFormDraft, storage: DraftStore | null = tabStorage()): void {
    try {
        storage?.setItem(KEY, JSON.stringify(draft))
    } catch {
        // A full or refused storage (a private window) only loses the kept form. The photo alone cannot fill it: a
        // picked photo is at most 90,000 characters (lib/avatar's AVATAR_MAX_CHARS), far under a tab's storage.
    }
}

/** Forgets the kept form: the teammate was added, or the form was cancelled. */
export function clearTeammateDraft(storage: DraftStore | null = tabStorage()): void {
    try {
        storage?.removeItem(KEY)
    } catch {
        // Nothing kept, or nothing to keep it in.
    }
}

/**
 * The kept form, taken out of the tab as it is put back, or null when there is none or it cannot be read. Anything
 * missing or malformed in it reads as the form's own starting value.
 */
export function takeTeammateDraft(storage: DraftStore | null = tabStorage()): TeammateFormDraft | null {
    try {
        const raw = storage?.getItem(KEY)
        if (!raw) return null
        storage?.removeItem(KEY)
        const saved = JSON.parse(raw) as Record<string, unknown>
        if (!saved || typeof saved !== 'object') return null
        const money = saved['money']
        return {
            job: draftJob(saved['job']),
            jobSearch: text(saved['jobSearch']),
            jobTitle: text(saved['jobTitle']),
            jobLine: text(saved['jobLine']),
            keptTaskIds: ids(saved['keptTaskIds']),
            swapChoices: choices(saved['swapChoices']),
            slotChoices: choices(saved['slotChoices']),
            name: text(saved['name']),
            nameTyped: flag(saved['nameTyped']),
            persona: text(saved['persona']),
            personaTyped: flag(saved['personaTyped']),
            teamId: text(saved['teamId']),
            aiProvider: saved['aiProvider'] === 'own' ? 'own' : 'shared',
            firstTask: text(saved['firstTask']),
            emailTyped: text(saved['emailTyped']),
            emailEdited: flag(saved['emailEdited']),
            avatar: typeof saved['avatar'] === 'string' && saved['avatar'] ? saved['avatar'] : null,
            money: typeof money === 'number' ? String(money) : text(money),
            moneyTyped: flag(saved['moneyTyped']),
            slugTyped: text(saved['slugTyped']),
            slugEdited: flag(saved['slugEdited']),
            extraTeamIds: ids(saved['extraTeamIds']),
            extraRoleIds: ids(saved['extraRoleIds']),
        }
    } catch {
        return null
    }
}
